import { Button, Drawer, Form, Icons, Space, Steps } from '@pankod/refine-antd'
import { useCreate, useCustom } from '@pankod/refine-core'
import React, { useCallback, useState } from 'react'

import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import SelfEnrollmentBasicConfigFormFields from './SelfEnrollmentBasicConfigFormFields'
import SelfEnrollmentLandingPageFormFields, {
  BlueprintData,
} from './SelfEnrollmentLandingPageFormFields'

type TSelfEnrollmentConfigForm = {
  quotaMaxProgram: number
  quotaMaxKelas: number
  isAutoApprove: boolean
  blueprintCourseId?: string
  title?: string
  description?: string
  detail?: string
  badge?: string
  category?: string
  provinceCodes?: string[]
  whitelistUsers?: string[]
}

type TSelfEnrollmentLandingPage = {
  blueprintCourseId: string
  blueprint?: {
    courseId: string
    title: string
    description: string
    imageUrl: string
  }
  title?: string
  description?: string
  detail?: string
  badge?: string
  category?: string
  provinceCodes?: string[]
  whitelistUsers?: string[]
}

type TSelfEnrollmentConfigResponse = {
  data: {
    diklatLandingPage: TSelfEnrollmentLandingPage
    configuration: {
      quotaMaxProgram?: number
      quotaMaxKelas?: number
      isAutoApprove?: boolean
    }
  }
}

const STEP1_FIELDS: (keyof TSelfEnrollmentConfigForm)[] = [
  'quotaMaxProgram',
  'quotaMaxKelas',
  'isAutoApprove',
]

const defaultBlueprintData: BlueprintData = {
  imageUrl: '',
  title: '-',
  description: '-',
}

export const useSelfEnrollmentConfigDrawer = () => {
  const [showData, setShowData] = useState<{
    show: boolean
    programId: string | number
  }>({ show: false, programId: '' })

  const onClose = useCallback(() => {
    setShowData((state) => ({
      ...state,
      show: false,
    }))
  }, [])

  return {
    show: (programId: string | number) => {
      setShowData({ show: true, programId })
    },
    visible: !!showData.show,
    programId: showData.programId,
    onClose,
  }
}

type SelfEnrollmentConfigDrawerProps = {
  visible: boolean
  onClose: () => void
  programId: string | number
  onSubmitted?: () => void
}

const SelfEnrollmentConfigDrawer = ({
  visible,
  onClose,
  programId,
  onSubmitted,
}: SelfEnrollmentConfigDrawerProps) => {
  const [form] = Form.useForm<TSelfEnrollmentConfigForm>()
  const [currentStep, setCurrentStep] = useState(0)
  const [hasVisitedStep2, setHasVisitedStep2] = useState(false)
  const [blueprintData, setBlueprintData] =
    useState<BlueprintData>(defaultBlueprintData)
  const [initialBlueprintOption, setInitialBlueprintOption] = useState<
    { label: string; value: string | number } | undefined
  >(undefined)

  const { mutateAsync: doUpsertSelfEnrollmentConfig, isLoading } = useCreate()
  const { isFetching: isFetchingInitialConfig } =
    useCustom<TSelfEnrollmentConfigResponse>({
      url: `${process.env.NEXT_PUBLIC_LMS_API_URL}/programs/${programId}/self-enrollment/config`,
      method: 'get',
      queryOptions: {
        enabled: visible && !!programId,
        onSuccess: (response) => {
          const data = response?.data?.data
          const config = data?.configuration ?? {}
          const landingPage = data?.diklatLandingPage ?? {}

          form.setFieldsValue({
            quotaMaxProgram: config.quotaMaxProgram,
            quotaMaxKelas: config.quotaMaxKelas,
            isAutoApprove: config.isAutoApprove ?? false,
            blueprintCourseId:
              landingPage.blueprintCourseId ?? landingPage.blueprint?.courseId,
            title: landingPage.title,
            description: landingPage.description,
            detail: landingPage.detail,
            badge: landingPage.badge,
            category: landingPage.category,
            provinceCodes: landingPage.provinceCodes,
            whitelistUsers: landingPage.whitelistUsers,
          })

          if (landingPage.blueprint) {
            setBlueprintData({
              imageUrl: landingPage.blueprint.imageUrl,
              title: landingPage.blueprint.title,
              description: landingPage.blueprint.description,
            })
            setInitialBlueprintOption({
              label: landingPage.blueprint.title,
              value: landingPage.blueprint.courseId,
            })
          }
        },
        onError: (error) => {
          showErrorNotification(
            error,
            'Terdapat gangguan saat mengambil konfigurasi pendaftaran mandiri',
          )
        },
      },
    })

  const handleNext = async () => {
    try {
      await form.validateFields(STEP1_FIELDS)
      setCurrentStep(1)
      setHasVisitedStep2(true)
    } catch {
      // validation errors shown inline
    }
  }

  const handleBack = () => {
    setCurrentStep(0)
  }

  const onFinish = async (values: TSelfEnrollmentConfigForm) => {
    const { category, provinceCodes, whitelistUsers, ...rest } = values
    const blueprintCourseId = rest.blueprintCourseId

    await doUpsertSelfEnrollmentConfig(
      {
        resource: `programs/${programId}/self-enrollment/config`,
        dataProviderName: 'lms',
        metaData: {
          httpMethod: 'put',
        },
        values: {
          diklatLandingPage: {
            blueprintCourseId,
            title: rest.title,
            description: rest.description,
            detail: rest.detail,
            badge: rest.badge,
            category,
            ...(category === 'PROVINCE' ? { provinceCodes } : {}),
            ...(category === 'WHITELIST_USER' ? { whitelistUsers } : {}),
          },
          configuration: {
            quotaMaxProgram: Number(rest.quotaMaxProgram),
            quotaMaxKelas: Number(rest.quotaMaxKelas),
            isAutoApprove: !!rest.isAutoApprove,
          },
        },
        successNotification: {
          message: 'Konfigurasi pendaftaran mandiri berhasil diperbarui',
          type: 'success',
          description: 'Sukses',
        },
        errorNotification: (error) =>
          showErrorNotification(
            error,
            'Terdapat gangguan saat menyimpan konfigurasi pendaftaran mandiri',
          ),
      },
      {
        onSuccess: () => {
          onSubmitted?.()
          onClose()
        },
      },
    )
  }

  return (
    <Drawer
      title="Kelola Pendaftaran Mandiri"
      visible={visible}
      onClose={onClose}
      destroyOnClose
      width="75%"
      maskClosable={false}
      afterVisibleChange={(isVisible) => {
        if (!isVisible) {
          form.resetFields()
          setCurrentStep(0)
          setHasVisitedStep2(false)
          setBlueprintData(defaultBlueprintData)
          setInitialBlueprintOption(undefined)
        }
      }}
      extra={
        <Space>
          {currentStep === 0 ? (
            <Button
              type="primary"
              onClick={handleNext}
              loading={isFetchingInitialConfig}
            >
              Lanjut <Icons.ArrowRightOutlined />
            </Button>
          ) : (
            <>
              <Button
                onClick={handleBack}
                icon={<Icons.ArrowLeftOutlined />}
                disabled={isLoading}
              >
                Kembali
              </Button>
              <Button
                icon={<Icons.SaveOutlined />}
                type="primary"
                loading={isLoading}
                onClick={() => form.submit()}
              >
                Simpan
              </Button>
            </>
          )}
        </Space>
      }
    >
      <Steps current={currentStep}>
        <Steps.Step title="Konfigurasi Dasar" />
        <Steps.Step title="Konfigurasi Landing Page" />
      </Steps>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          isAutoApprove: false,
        }}
        onFinish={onFinish}
        validateMessages={{ required: '${label} harus diisi.' }}
      >
        <div
          className="mt-8"
          style={{ display: currentStep === 0 ? 'block' : 'none' }}
        >
          <SelfEnrollmentBasicConfigFormFields form={form} />
        </div>

        {hasVisitedStep2 && (
          <div
            className="mt-8"
            style={{ display: currentStep === 1 ? 'block' : 'none' }}
          >
            <SelfEnrollmentLandingPageFormFields
              form={form}
              visible={visible && currentStep === 1}
              blueprintData={blueprintData}
              initialBlueprintOption={initialBlueprintOption}
              onBlueprintSelect={(data) =>
                setBlueprintData(data || blueprintData)
              }
            />
          </div>
        )}
      </Form>
    </Drawer>
  )
}

export default SelfEnrollmentConfigDrawer
